import type { SearchEntityRecordsInfiniteQueryOptions } from '@kizen/query/records';
import { searchEntityRecordsInfiniteQuery } from 'query/records';

type Options = {
  additionalFilters: Record<string, any>[];
  blockFilters?: {
    and: boolean;
    query: {
      and: boolean;
      filters: Record<string, any>[];
    }[];
  };
  recordId?: string;
  customObjectId: string;
  enabled?: boolean;
  fieldIds?: string[];
  ordering?: string;
  pageSize?: number;
  search?: string;
  searchWithinFieldIds?: string[];
};

export const getEntityRecordsSearchQuery = (
  options: Options
): ReturnType<typeof searchEntityRecordsInfiniteQuery> => {
  const {
    additionalFilters,
    blockFilters,
    recordId = '',
    customObjectId = '',
    fieldIds = [],
    searchWithinFieldIds = [],
    search,
    ordering,
    pageSize = 20,
    enabled = true,
  } = options;

  const body: SearchEntityRecordsInfiniteQueryOptions['body'] = {
    and: true,
    query: [
      {
        and: false,
        filters: additionalFilters,
      },
    ],
  };

  const params: SearchEntityRecordsInfiniteQueryOptions['params'] = {
    page_size: pageSize,
  };

  if (searchWithinFieldIds.length) {
    body.search_within_field_ids = searchWithinFieldIds;
  }

  if (fieldIds.length) {
    body.field_ids = fieldIds;
  }

  if (ordering) {
    params.ordering = ordering;
  }

  if (search) {
    params.search = search;
  }

  if (blockFilters?.query?.length) {
    body.query!.push(blockFilters);
  }

  return searchEntityRecordsInfiniteQuery(customObjectId, {
    enabled: enabled && Boolean(customObjectId),
    params,
    body,
    scope: ['record-details', 'related-object-block', recordId],
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.next) {
        return new URL(lastPage.next).searchParams.get('page');
      }
    },
    getPreviousPageParam: (previousPage) => {
      if (previousPage.previous) {
        return new URL(previousPage.previous).searchParams.get('page');
      }
    },
    retry: false,
    throwOnError: false,
    staleTime: Infinity,
    placeholderData: {
      pageParams: [1],
      pages: [
        {
          count: 0,
          results: [],
          next: null,
          previous: null,
        },
      ],
    },
  });
};
